import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  FILTER_CONTENT_ITEM,
  ISelectInput,
  SearchSelector,
  SearchOperator,
} from '@portal/shared/ui/filter';
import { FilterStaticBase } from '@portal/shared/ui/filter/src/lib/components/static-base';
import { TransactionFilterList } from '../transaction-filters.list';
import { TransactionHeaderStates } from './header-state.list';
import { FiltersService } from '@portal/shared/ui/filter/src/lib/services/filters.service';
import { TransactionFilter } from '../../../enums/transaction-filter.enum';
import cloneDeep from 'lodash/cloneDeep';
import { SupportedProcessorSettingsService } from '@apps/portal/src/app/services/supported-processor-settings.service';
import { Subscription } from 'rxjs';
import { Directions } from '@portal/shared/core/src/lib/enums/direction.enum';

@Component({
  selector: 'portal-transaction-filter-header-state',
  templateUrl: './header-state.component.html',
  providers: [
    {
      provide: FILTER_CONTENT_ITEM,
      useExisting: FilterTransactionHeaderStateComponent,
    },
  ],
  standalone: false,
})
export class FilterTransactionHeaderStateComponent extends FilterStaticBase {
  @Input()
  isMainDropdownFilter: boolean;
  @Input()
  showFilterSubMenu: boolean;
  @Output() headerStateTextName: EventEmitter<string> = new EventEmitter();
  transactionStateText = this.transactionfilters.list.find(
    (filter) => filter.id === TransactionFilter.TransactionState,
  ).text;
  readonly Directions = Directions;

  constructor(
    private headerStates: TransactionHeaderStates,
    private transactionfilters: TransactionFilterList,
    private filtersService: FiltersService,
    private supportedProcessorSettingsService: SupportedProcessorSettingsService,
  ) {
    super(
      SearchSelector.DisplayTransactionState,
      headerStates.supportedTransactionStatusFromProcessorSettings(),
    );
    this.intermediatePreSelectedEntities = new Map();
    this.addButtonLabel = $localize`Add`;
    this.supportedProcessorSettingsService.isProcessorFetched$.subscribe(() => {
      this.setAllEntities(headerStates.supportedTransactionStatusFromProcessorSettings());
    });
    this.filtersService.actionRemoveFilter$.subscribe((entityToRemove: ISelectInput) => {
      if (TransactionFilter.TransactionState === entityToRemove.key) {
        const selectedEntity = this.localPreSelectedEntities.get(entityToRemove.id);
        if (selectedEntity?.id) {
          this.onEntityRemoved(entityToRemove);
          this.onFilterApplied();
          this.itemToggled.emit();
        }
      }
    });

    this.subMenuComponentSelected$.subscribe((submenu) => {
      if (SearchSelector.DisplayTransactionState.includes(submenu)) {
        this.synchronizeLocalPreSelectedEntities();
      }
      this.updateEntities();
    });

    this.updateEntities$.subscribe(() => {
      if (this.isMainDropdownFilter) {
        this.synchronizeLocalPreSelectedEntities();
      }
      this.updateEntities();
    });

    this.updateCounter$.subscribe(() => {
      const localPreSelected: Map<string, ISelectInput> = new Map();
      if (this.criteriaMapPreSelected.has(SearchSelector.DisplayTransactionState)) {
        const argumentArr = this.getArgumentArray(
          this.criteriaMapPreSelected,
          SearchSelector.DisplayTransactionState,
        );

        if (this.criteriaMapPreSelected.get(SearchSelector.DisplayTransactionState)?.argument) {
          argumentArr?.forEach((argument) => {
            localPreSelected.set(argument, {
              id: argument,
              text: this.headerStates.getValue(argument),
            });
          });
          this.localPreSelectedEntities = cloneDeep(localPreSelected);
        } else {
          this.localPreSelectedEntities.clear();
        }
      }
      this.updateEntities();
    });
  }

  addIntermediateEntity(selectedEntity: ISelectInput): void {
    this.intermediatePreSelectedEntities.set(selectedEntity.id, selectedEntity);
    this.setAddButtonLabel();
  }

  onEntityRemoved(selectedEntity: ISelectInput): void {
    this.intermediatePreSelectedEntities.delete(selectedEntity.id);
    this.setAddButtonLabel();
  }

  onFilterAddedAndEntitiesUpdated(): void {
    this.localPreSelectedEntities.clear();
    this.intermediatePreSelectedEntities.forEach((entity) => {
      this.localPreSelectedEntities.set(entity.id, entity);
    });
    this.onFilterAdded();
    this.updateEntities();
    if (this.isOpen) {
      this.closeSubmenus$.next(true);
    }
    this.headerStateTextName.emit(this.transactionStateText);
    this.isOpen = false;
  }

  onFilterAdded(): void {
    this.updateEntities();
    const values: string[] = this.getValues();
    if (values.length) {
      this.filterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        operator: SearchOperator.In,
        value: Array.from(this.localPreSelectedEntities.values()),
        argument: `(${Array.from(values).join(',')})`,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        value: Array.from(this.localPreSelectedEntities.values()),
      });
    } else {
      this.filterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        toRemove: true,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        toRemove: true,
      });
    }

    this.searchedText = '';
    this.itemToggled.emit();
    this.updateSwitchToggleState();
  }

  protected override subscribeToRemoveEntityObservable(): Subscription {
    return this.entityRemoved$.subscribe((entityToRemove: ISelectInput) => {
      const selectedEntity = this.localPreSelectedEntities.get(entityToRemove.id);
      if (selectedEntity) {
        this.onEntityRemoved(entityToRemove);
        this.onFilterAdded();
        this.isOpen = false;
      }
    });
  }

  private synchronizeLocalPreSelectedEntities(): void {
    if (this.criteriaMapPreSelected?.get(SearchSelector.DisplayTransactionState)?.argument) {
      const argumentArr = this.getArgumentArray(
        this.selectedEntities,
        SearchSelector.DisplayTransactionState,
      );

      this.localPreSelectedEntities.clear();
      this.intermediatePreSelectedEntities.clear();
      argumentArr?.forEach((argument) => {
        this.setLocalPreSelectedEntities(argument, this.headerStates.getValue(argument));
      });
      this.localPreSelectedEntities.forEach((entity) => {
        this.intermediatePreSelectedEntities.set(entity.id, entity);
      });
    } else {
      this.localPreSelectedEntities.clear();
      this.intermediatePreSelectedEntities.clear();
    }
    this.updateEntities();
    this.setAddButtonLabel();
  }
  onClear(): void {
    this.clearStaticMultiSelect();
    this.entities = this.headerStates.supportedTransactionStatusFromProcessorSettings();
    this.removeStaticFilter();
    this.headerStateTextName.emit(this.transactionStateText);
  }
}
